import { translate } from '../../common/language/translations';
import { staticTeamKeys } from '../static/constants/teams';
import { Club } from '../types/club';
import { CompetitionMap } from '../types/competition';
import { TeamMap } from '../types/team';


// get player name to display, where the first name pattern that is not longer than maxLength is returned
// the patterns are ordered <name>, <firstName><initials><lastName>, <firstName><lastName>, <initials><lastName>
// if name does not contain a space, the name is returned regardless of length
// if none of the three first patterns are short enough, the shortest pattern is returned unless shortestNameIsNotAnOption is true
export const getDisplayPlayerName = (
  name: string,
  maxLength?: number,
  shortestNameIsNotAnOption?: boolean,
) => {
  if (name === undefined) return '';

  if (maxLength === undefined || name.length <= maxLength) {
    return name;
  }

  const playerName = name.trim().replace('"', '');
  const nameList = playerName.split(' ');

  if (nameList.length === 1) {
    return name;
  }

  const firstName = nameList[0];
  const lastName = nameList[nameList.length - 1];
  const initials = nameList.slice(1, nameList.length - 1).map(nameEntry => nameEntry[0]).join('. ') + '.';

  const nameWithInitials = `${firstName} ${initials} ${lastName}`;
  if (nameWithInitials.length <= maxLength) {
    return nameWithInitials;
  }

  const nameWithoutInitials = `${firstName} ${lastName}`;
  if (shortestNameIsNotAnOption || nameWithoutInitials.length <= maxLength) {
    return nameWithoutInitials;
  }

  const shortestName = `${firstName[0]}. ${lastName}`;
  if (shortestName.length <= maxLength) {
    return shortestName;
  }

  if (lastName.includes('-')) {
    if (lastName.length <= maxLength) {
      return lastName;
    }

    const lastNameList = lastName.split('-');
    return `${firstName[0]}. ${lastNameList[lastNameList.length - 1]}`;
  }

  return shortestName;
};


// get shortened user name
export const getShortUserName = (name: string) => {
  if (!name) return '';

  const nameList = name.split(' ');

  if (nameList.length === 1 || name.length < 10) {
    return name;
  }

  const firstName = nameList[0];
  const lastName = nameList[nameList.length - 1];

  if (firstName.length + lastName.length < 14) {
    return `${firstName} ${lastName}`;
  }

  return `${firstName} ${lastName[0]}.`;
};


// get shortened file name
export const getShortenedFileName = (fileName: string, maxLength?: number) => {
  maxLength = maxLength ?? 32;
  if (fileName.length > maxLength) {
    const halfMaxLength = Math.round(maxLength / 2) - 1;
    return fileName.slice(0, halfMaxLength) + '...' + fileName.slice(fileName.length - halfMaxLength, fileName.length);
  } else {
    return fileName;
  }
};


// get the display name of a team
export const getDisplayTeamName = (
  teamId: string,
  teams: TeamMap | null,
  language: string | undefined,
  teamName: string | undefined, // activities/archivedTeams will have a team name stored with lower precedence
) => {

  // static teams are translated
  if (staticTeamKeys.includes(teamId)) {
    return translate(teamId, language);
  }

  // if the team currently exists, use the current team name
  if (teams && teamId in teams) {
    return teams[teamId].name;
  }

  return teamName;
};


// get the name of the current competition of the provided club
export const getCurrentCompetitionName = (
  club: Club | undefined,
  competitions: CompetitionMap
) => {
  if (club && club.current_competition_id) {
    return competitions[club.current_competition_id]?.name ?? '-';
  }
  return '-';
};
